/**
 * The wall's project feed in this window (Part 04 decision 3). Not
 * mirrored: each window pages on its own (`lib/wall-feed-loader.ts`). The
 * cards are the frames store's wall members at or after the loaded boundary,
 * newest first, so new frames enter at the top and deleted, moved or
 * archived ones leave by themselves.
 */
import { create } from "zustand";
import type { FrameSummaryPage } from "@/api/frame-listing";
import { useEqualSelector } from "@/hooks/use-equal-selector";
import type { FrameData } from "@/lib/frame-data";
import { sameIds } from "@/lib/same-ids";
import {
  buildWallContext,
  isProjectTool,
  wallPlacement,
  wallToolsRow,
  type WallContext,
} from "@/lib/wall-membership";
import { selectOrderedFrames } from "@/stores/frames-selectors";
import { useFramesStore, type FramesState } from "@/stores/frames-store";

/** A frame's place in server order. */
export interface FramePosition {
  createdAt: string;
  frameId: string;
}

export interface WallFeedState {
  projectId: string | null;
  /** The frames-store generation the pages were loaded into; null before any. */
  builtForGeneration: number | null;
  /** The oldest loaded wall frame; null until a page brought one. */
  boundary: FramePosition | null;
  cursor: string | null;
  hasMore: boolean;
  pagesLoaded: number;
  loading: boolean;
  /** The last page did not land; the next `beginPage` clears it. */
  failed: boolean;
  /** Identifies the page in flight; a reset makes it stale. */
  pageToken: number;
  /** Where the Pinned row's page last landed; null before any. */
  toolsLoadedFor: { projectId: string; generation: number } | null;
  markToolsLoaded: (projectId: string, generation: number) => void;
  /** Where the Pinned row's page last failed to land; null before any. */
  toolsFailedFor: { projectId: string; generation: number } | null;
  markToolsFailed: (projectId: string, generation: number) => void;
  /** Start over from the first page. */
  reset: (projectId: string, generation: number) => void;
  /** Mark a page in flight; returns its token. */
  beginPage: () => number;
  /**
   * End the page `token` started; `null` when it failed or went stale. A
   * stale page marks the feed failed only until the reset that follows it.
   */
  finishPage: (token: number, page: FrameSummaryPage | null) => void;
}

const EMPTY_FEED = {
  boundary: null,
  cursor: null,
  hasMore: true,
  pagesLoaded: 0,
  loading: false,
  failed: false,
};

export const useWallFeedStore = create<WallFeedState>()((set, get) => ({
  projectId: null,
  builtForGeneration: null,
  ...EMPTY_FEED,
  pageToken: 0,
  toolsLoadedFor: null,
  toolsFailedFor: null,

  markToolsLoaded: (projectId, generation) =>
    set({ toolsLoadedFor: { projectId, generation } }),
  markToolsFailed: (projectId, generation) =>
    set({ toolsFailedFor: { projectId, generation } }),

  reset: (projectId, generation) =>
    set((state) => ({
      ...EMPTY_FEED,
      projectId,
      builtForGeneration: generation,
      pageToken: state.pageToken + 1,
    })),

  beginPage: () => {
    const pageToken = get().pageToken + 1;
    set({ pageToken, loading: true, failed: false });
    return pageToken;
  },

  finishPage: (token, page) =>
    set((state) => {
      if (token !== state.pageToken) return state;
      if (!page) return { loading: false, failed: true };
      const oldest = page.items[page.items.length - 1];
      return {
        loading: false,
        boundary: oldest
          ? { createdAt: oldest.created_at, frameId: oldest.id }
          : state.boundary,
        cursor: page.nextCursor,
        hasMore: page.hasMore,
        pagesLoaded: state.pagesLoaded + 1,
      };
    }),
}));

type FeedWindow = Pick<
  WallFeedState,
  "builtForGeneration" | "boundary" | "hasMore" | "pagesLoaded"
> &
  Partial<Pick<WallFeedState, "projectId">>;

/**
 * The loaded frames of the wall's project, in server order. The store also
 * holds a card Denker presented from another project, for the window or
 * Focus showing it; it is never the wall's.
 */
function projectFrames(
  frames: Pick<FramesState, "framesById" | "frameOrder">,
  projectId: string | null | undefined,
): FrameData[] {
  return selectOrderedFrames(frames).filter(
    (frame) => !projectId || !frame.spaceId || frame.spaceId === projectId,
  );
}

function isBefore(frame: FrameData, position: FramePosition): boolean {
  if (frame.createdAt !== position.createdAt) {
    return frame.createdAt < position.createdAt;
  }
  return frame.frameId < position.frameId;
}

/**
 * The feed's frame ids, newest first. Reads only membership fields (type,
 * archive, pin, position), so content patches return an equal array. Empty
 * while the feed was built on another generation (it is about to reset).
 */
export function selectWallFrameIds(
  frames: Pick<FramesState, "framesById" | "frameOrder" | "generation">,
  feed: FeedWindow,
): string[] {
  if (feed.builtForGeneration !== frames.generation) return [];
  if (feed.pagesLoaded === 0) return [];
  const ordered = projectFrames(frames, feed.projectId);
  const ctx = buildWallContext(ordered);
  const lowest = feed.hasMore ? feed.boundary : null;
  const ids: string[] = [];
  for (let index = ordered.length - 1; index >= 0; index -= 1) {
    const frame = ordered[index]!;
    if (lowest && isBefore(frame, lowest)) break;
    if (wallPlacement(frame, ctx) === "wall") ids.push(frame.frameId);
  }
  return ids;
}

let contextMemo: {
  frames: Pick<FramesState, "framesById" | "frameOrder">;
  projectId: string | null;
  ctx: WallContext;
} | null = null;

/**
 * The membership context of the loaded frames, built once per frames change
 * however many cards read it.
 */
export function selectWallContext(
  frames: Pick<FramesState, "framesById" | "frameOrder">,
): WallContext {
  const { projectId } = useWallFeedStore.getState();
  if (
    contextMemo?.frames.framesById === frames.framesById &&
    contextMemo.frames.frameOrder === frames.frameOrder &&
    contextMemo.projectId === projectId
  ) {
    return contextMemo.ctx;
  }
  const ctx = buildWallContext(projectFrames(frames, projectId));
  contextMemo = {
    frames: { framesById: frames.framesById, frameOrder: frames.frameOrder },
    projectId,
    ctx,
  };
  return ctx;
}

/**
 * Whether frame `frameId` is the Project's own task board or memory, not a
 * user's pin: its tile has no hover pill and its menu no Delete.
 */
export function selectIsProjectTool(
  frames: Pick<FramesState, "framesById" | "frameOrder">,
  frameId: string,
): boolean {
  const frame = frames.framesById[frameId];
  return !!frame && isProjectTool(frame, selectWallContext(frames));
}

/**
 * The Pinned rows' frame ids from the loaded frames
 * (`lib/wall-feed-loader.ts` `loadWallTools` loads every tool and fitting
 * pin). Reads only membership fields, like `selectWallFrameIds`.
 */
export function selectWallToolIds(
  frames: Pick<FramesState, "framesById" | "frameOrder">,
): string[] {
  return wallToolsRow(selectWallContext(frames));
}

/** The Pinned rows, re-rendering only when their frames change. */
export function useWallToolIds(): readonly string[] {
  return useFramesStore(useEqualSelector(selectWallToolIds, sameIds));
}

/** This window's project feed, re-rendering only when its ids change. */
export function useWallFrameIds(): readonly string[] {
  const builtForGeneration = useWallFeedStore((s) => s.builtForGeneration);
  const boundary = useWallFeedStore((s) => s.boundary);
  const hasMore = useWallFeedStore((s) => s.hasMore);
  const pagesLoaded = useWallFeedStore((s) => s.pagesLoaded);
  const projectId = useWallFeedStore((s) => s.projectId);
  return useFramesStore(
    useEqualSelector(
      (state: FramesState) =>
        selectWallFrameIds(state, {
          builtForGeneration,
          boundary,
          hasMore,
          pagesLoaded,
          projectId,
        }),
      sameIds,
    ),
  );
}
